import { AHText, Box, useScaleFactor } from "arkh";
import {
  ActivityIndicator,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PixelRatio,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import React, {
  ForwardedRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { TodoOverviewListItem } from "./TodoOverviewListItem";
import { useWidgetTodoSubscription } from "./useWidgetTodoSubscription";
import { useEvent } from "../useEvent";
import { graphql, usePaginationFragment } from "react-relay";
import { WidgetTodoOverviewListPaginated$key } from "./__generated__/WidgetTodoOverviewListPaginated.graphql";

const { containerStyle } = StyleSheet.create({
  containerStyle: { paddingBottom: 50 },
});
export type WidgetTodoOverviewListRef = {
  scrollToItem: (itemId: string | null) => void;
};
const WidgetTodoOverviewListC = (
  props: {
    widget: WidgetTodoOverviewListPaginated$key;
    isFocused?: boolean;
    interactive?: boolean;
    mode: "FOCUS" | "IDLE";
    onPressItem: (id: string) => void;
    setScroll: (itemId: string) => void;
    highlightedItemId?: string;
    hideShowMoreButton?: boolean;
  },
  ref: ForwardedRef<WidgetTodoOverviewListRef>
) => {
  const {
    data: widget,
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment(
    graphql`
      fragment WidgetTodoOverviewListPaginated on WidgetTodoOverview
      @refetchable(queryName: "WidgetTodoOverviewRefetchQuery") {
        id
        title
        category {
          id
          title
        }
        teams {
          id
        }
        allTodos(first: $first, after: $after)
          @connection(key: "WidgetTodoOverview__allTodos") {
          __id
          edges {
            node {
              id
              ordinal
              status
              ...TodoOverviewListItemWidget_Todo
            }
          }
        }
      }
    `,
    props.widget
  );

  const todos = useMemo(() => {
    return (widget?.allTodos?.edges ?? [])
      .map((edge) => edge.node)
      .filter((node) => node)
      .sort((a, b) => (b.ordinal > a.ordinal ? 1 : -1));
  }, [widget]);

  const shared = useMemo(
    () => ({
      opacity: props.mode === "IDLE" ? 0.2 : 1,
    }),
    [props.mode]
  );

  const renderRow = useCallback(
    ({ item, index }: { item: (typeof todos)[number]; index: number }) => (
      <View style={item.status !== "PENDING" ? shared : undefined}>
        <TodoOverviewListItem
          isFocused={item.id === props.highlightedItemId}
          isBottomItem={index + 1 === todos.length}
          isTopItem={index === 0}
          todo={item}
          onPress={props.onPressItem}
        />
      </View>
    ),
    [shared, props.highlightedItemId, props.onPressItem, todos.length]
  );

  const keyExtractor = useCallback((item: (typeof todos)[0]) => item.id, []);

  const todoIds = useMemo(() => todos.map(({ id }) => id), [todos]);
  const teamIds = useMemo(
    () => widget?.teams?.map(({ id }) => id) ?? [],
    [widget.teams]
  );

  const categoryId = widget.category?.id;

  useWidgetTodoSubscription(
    teamIds,
    todoIds,
    categoryId,
    widget.allTodos?.__id ?? undefined,
    props.interactive
  );

  const onEndReached = useEvent(() => {
    if (hasNext && !isLoadingNext && props.interactive !== false) {
      loadNext(10);
    }
  });
  const sf = useScaleFactor();
  const ITEM_HEIGHT = useMemo(
    () => PixelRatio.roundToNearestPixel(40 * sf),
    [sf]
  );
  const getItemLayout = useCallback(
    (_data: any, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    [ITEM_HEIGHT]
  );

  const flatListRef = useRef<FlatList>(null);
  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId) => {
      if (itemId) {
        const index = todos.findIndex((todo) => todo.id === itemId);
        if (index !== -1) {
          flatListRef.current?.scrollToIndex({ index, animated: true });
        }
      } else {
        flatListRef.current?.scrollToOffset({
          offset: 0,
          animated: true,
        });
      }
    },
  }));

  const setScroll = props.setScroll;
  const lastScrollId = useRef<string | null>(null);
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const index = Math.max(
        0,
        Math.floor(
          (event.nativeEvent.targetContentOffset
            ? event.nativeEvent.targetContentOffset.y
            : event.nativeEvent.contentOffset.y) /
            ITEM_HEIGHT -
            1
        )
      );
      const item = todos[index];
      if (item && lastScrollId.current !== item.id) {
        setScroll(item.id);
        lastScrollId.current = item.id;
      }
    },
    [ITEM_HEIGHT, setScroll, todos]
  );
  const ListFooterComponent = useMemo(
    () => (
      <Box style={{ flex: 1 }} spaceTop="xxlarge">
        {isLoadingNext ? <ActivityIndicator /> : null}
        {props.interactive !== false &&
        hasNext &&
        !isLoadingNext &&
        !props.hideShowMoreButton ? (
          <AHText textAlign="center">Ladda mer</AHText>
        ) : null}
      </Box>
    ),
    [hasNext, isLoadingNext, props.hideShowMoreButton, props.interactive]
  );

  return (
    <FlatList
      ref={flatListRef}
      initialNumToRender={10}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      ListFooterComponent={ListFooterComponent}
      keyExtractor={keyExtractor}
      data={todos}
      scrollEventThrottle={200}
      onScroll={props.isFocused ? onScroll : undefined}
      renderItem={renderRow}
      scrollEnabled={!(props.interactive === false)}
      onEndReached={onEndReached}
      getItemLayout={getItemLayout}
      contentContainerStyle={containerStyle}
    />
  );
};

export const WidgetTodoOverviewList = React.memo(
  React.forwardRef(WidgetTodoOverviewListC)
);
